import { getEstateByCLBUuid } from '../../../api/realEstate/realEstateApi'
import { ContentStateInterface, ObjectParams, SaveType } from '../../../@types/types'
import { showMessage } from '../../reducers/message/messageFeature'
import { getAttachmentsInitialCompletionSuccess } from '../../reducers/multimedia/multimediaFeature'
import {
  getInsertionGeoCoordinatesCompletionSuccess,
  getInsertionInternationalAddressCompletionSuccess,
} from '../../reducers/address/addressFeature'
import { tracking, trackingOnPageLoad } from '../../../tracking/tracking'
import { FlowInformation, FlowName } from '../../../@types/flow-types'
import { configureFlow } from '../flowInformation/flowInformation'
import { InitState } from '../../reducers/root/initialState'
import { getDefaultLogger } from '../../../services/logger/logger'
import { loadSuggestionsIfNeeded } from '../suggestedText/prepareLoadSuggestions'
import { ValidationSchema } from '../../../@types/validation-types'
import { initialManualGeoCodingLoading } from '../gisService/initialManualGeoCodingLoading'
import { doAutoSaveDecider } from './doAutoSaveDecider'
import { AppDispatch } from '../../../@types/store'
import { validateCurrentInsertion } from '../insertionData/insertionData'
import { findObjects } from '../../../api/object/objectApi'
import { getInsertionInitialCompletionSuccess as textGeneratedInsertionInitialCompletionSuccess } from '../../reducers/suggestedText/suggestedTextFeature'
import {
  getInsertionEmptyInitialCompletionSuccess,
  getInsertionInitialCompletionSuccess,
  getObjectInitialCompletionSuccess,
  insertionUpdateSuccess,
} from '../../reducers/insertion/insertionFeature'

export const initInsertionEditor =
  (
    flowInformation: FlowInformation,
    defaultStateValues: ContentStateInterface,
    id?: string,
    objectParams?: ObjectParams
  ) =>
  async (dispatch: AppDispatch, getState: () => InitState) => {
    const isSaving = getState().insertionFeature.save == SaveType.SAVING
    if (isSaving) {
      dispatch(insertionUpdateSuccess())
    }
    await configureFlow(flowInformation)(dispatch)
    const objectChooserInformation = getState().flowFeature.objectChooserInformation
    const { objectSelected, insertionFlowDataLoaded: hasUserObjectsLoaded } = objectChooserInformation

    if (!hasUserObjectsLoaded) {
      if (id) {
        await loadListing(id, defaultStateValues, objectParams)(dispatch, getState)
      } else if (objectParams) {
        await loadObject(objectParams, defaultStateValues)(dispatch)
      } else {
        dispatch(getInsertionEmptyInitialCompletionSuccess({ insertionData: defaultStateValues }))
      }
    }
    if (hasUserObjectsLoaded && !objectSelected) {
      dispatch(getInsertionEmptyInitialCompletionSuccess({ insertionData: defaultStateValues }))
    }
    //TODO: Test missing
    dispatch(validateCurrentInsertion())

    trackingOnPageLoad(
      flowInformation,
      getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
      undefined,
      'HPEP-1579-gen_ai'
    )
  }

export const loadListing =
  (id: string, defaultStateValues: ContentStateInterface, objectsParams?: ObjectParams) =>
  async (dispatch: AppDispatch, getState: () => InitState) => {
    try {
      let realEstate = await getEstateByCLBUuid(id, defaultStateValues)
      const { name } = getState().flowFeature.flowInformation
      if (name === FlowName.TENANT_FLAT_SHARE_ROOM_RENT && realEstate && realEstate.listing.freeUntil === undefined) {
        realEstate = { ...realEstate, listing: { ...realEstate.listing, intermediateRent: false } }
      }

      if (realEstate) {
        const { manualAddress, meta, geoCoordinates, attachments, listing } = realEstate

        dispatch(
          getInsertionInitialCompletionSuccess({
            insertionData: listing,
            insertionIdentification: meta,
            geoCoordinates: geoCoordinates,
          })
        )

        dispatch(textGeneratedInsertionInitialCompletionSuccess({ insertionData: listing }))

        geoCoordinates && dispatch(getInsertionGeoCoordinatesCompletionSuccess({ geoCoordinates: geoCoordinates }))
        attachments && dispatch(getAttachmentsInitialCompletionSuccess({ initAttachments: attachments }))

        if (manualAddress?.manualGeoCoding) {
          await initialManualGeoCodingLoading()(dispatch, getState)
        }
        // To update the validation schema accordingly
        const insertionDataFromStore = getState().insertionFeature.insertionData
        if (
          insertionDataFromStore.manualCountry !== undefined &&
          insertionDataFromStore.manualCountry.value !== undefined &&
          insertionDataFromStore.manualCountry.value !== null
        ) {
          dispatch(getInsertionInternationalAddressCompletionSuccess())
        }
      } else if (objectsParams) {
        await loadObject(objectsParams, defaultStateValues)(dispatch)
      } else {
        dispatch(getInsertionEmptyInitialCompletionSuccess({ insertionData: defaultStateValues }))
      }
    } catch (e) {
      getDefaultLogger().error('failed to load listing', { e })
      dispatch(showMessage({ message: 'unknown' }))
    }
  }
const findObject = async (defaultStateValues: ContentStateInterface, objectGatewayParams: ObjectParams) => {
  const objectGatewayResponse = await findObjects(defaultStateValues, objectGatewayParams)
  return objectGatewayResponse ? objectGatewayResponse[0] : null
}

export const loadObject =
  (objectGatewayParams: ObjectParams, defaultStateValues: ContentStateInterface) => async (dispatch: AppDispatch) => {
    try {
      const realEstate = await findObject(defaultStateValues, objectGatewayParams)
      if (realEstate) {
        const { meta, geoCoordinates, attachments, listing } = realEstate
        dispatch(getObjectInitialCompletionSuccess({ insertionData: listing, insertionIdentification: meta }))
        geoCoordinates && dispatch(getInsertionGeoCoordinatesCompletionSuccess({ geoCoordinates: geoCoordinates }))
        attachments && dispatch(getAttachmentsInitialCompletionSuccess({ initAttachments: attachments }))
      } else {
        dispatch(getInsertionEmptyInitialCompletionSuccess({ insertionData: defaultStateValues }))
      }
    } catch (e) {
      getDefaultLogger().error('failed to load object', { e })
      dispatch(showMessage({ message: 'unknown' }))
    }
  }

export const doNavigate =
  (validationSchema: ValidationSchema) => async (dispatch: AppDispatch, getState: () => InitState) => {
    const { changed } = getState().insertionFeature

    await loadSuggestionsIfNeeded(dispatch, getState, validationSchema)
    // TODO: check if data need to updated here
    changed && doAutoSaveDecider(validationSchema)(dispatch, getState)
  }

export const doTracking =
  (flowInformation: FlowInformation, eventLabel: string, immediately: boolean = false, eventParameter?: string) =>
  (_: AppDispatch, getState: () => InitState) => {
    tracking(
      {
        flowInformation: flowInformation,
        eventName: eventLabel,
        entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
        eventParameter: eventParameter,
      },
      immediately
    )
  }
